import { PartNo, QuantityAndPriceMonthTable, YearData } from '../contexts/types';
import i18n from '../utils/i18n';
import { resolveDisplayPartNoByKey } from '../utils/utils';

/** 翻訳キーの共通プレフィックス */
const T_PREFIX = 'features.InvestmentScreening.QuantityAndPrice';

/**
 * Toast関数の型
 */
type ToastFunction = (options: {
  title: string;
  description: string;
  status: 'error' | 'warning' | 'success' | 'info';
  duration: number;
  isClosable: boolean;
}) => void;

/**
 * SpreadSheetデータ→コンポーネントデータ変換結果
 */
export interface QuantityAndPriceConversionResult {
  data: { [key: number]: YearData };
  maxYearByEOL: number;
  errors: string[];
  warnings: string[];
}

/**
 * 販売数量・販売単価データをSpreadSheet書き込み用2D配列に変換（1パーツ分）
 *
 * @param table QuantityAndPriceMonthTable
 * @param baseStartYear 起算年（会計年度ベース）
 * @param projectStartDate プロジェクト開始日（YYYY-MM-DD）
 * @returns [headerRow, quantityRow, priceRow] の2D配列
 */
export const quantityAndPriceToSpreadSheetData = (
  table: QuantityAndPriceMonthTable,
  baseStartYear: number,
  projectStartDate?: string | null,
  isGeneralPurpose?: boolean
): any[][] => {
  const startMonth = projectStartDate
    ? parseInt(projectStartDate.split("-")[1], 10)
    : 4;

  const spreadsheetHeader: (string | number)[] = [""];
  const quantityRow: (string | number)[] = [i18n.t(`${T_PREFIX}.label.${isGeneralPurpose ? "quantityAssumed" : "quantitySales"}`)];
  const priceRow: (string | number)[] = [i18n.t(`${T_PREFIX}.label.${isGeneralPurpose ? "priceAssumed" : "priceSales"}`)];

  table.years.forEach((y: number) => {
    Object.values(table.data[y].months).forEach((m: any) => {
      const fiscalYear =
        m.month >= 4 && m.month <= 12
          ? baseStartYear + y - 1
          : baseStartYear + y;

      const startYear =
        startMonth >= 4 && startMonth <= 12
          ? baseStartYear
          : baseStartYear + 1;

      if (
        fiscalYear < startYear ||
        (fiscalYear === startYear && m.month < startMonth)
      ) {
        return;
      }

      const ym = `${fiscalYear}/${String(m.month).padStart(2, "0")}`;
      spreadsheetHeader.push(ym);
      quantityRow.push(m.quantity ? m.quantity : 0);
      priceRow.push(m.price ? m.price : 0);
    });
  });

  return [spreadsheetHeader, quantityRow, priceRow];
};

/**
 * SpreadSheetのヘッダー（年月）のバリデーション
 *
 * @param header ヘッダー配列（最初の空セルは除外済み）
 * @returns バリデーション結果
 */
export const validateQuantityAndPriceHeader = (header: string[]): boolean => {
  const ymRegex = /^\d{4}\/(0?[1-9]|1[0-2])$/;

  for (let i = 0; i < header.length; i++) {
    const ym = header[i];

    // フォーマットチェック
    if (!ymRegex.test(ym)) {
      return false;
    }

    // 連続性チェック
    if (i > 0) {
      const [prevYear, prevMonth] = header[i - 1].split("/").map(Number);
      const [currYear, currMonth] = ym.split("/").map(Number);

      const nextMonth = prevMonth === 12 ? 1 : prevMonth + 1;
      const nextYear = prevMonth === 12 ? prevYear + 1 : prevYear;

      if (currYear !== nextYear || currMonth !== nextMonth) {
        return false;
      }
    }
  }

  return true;
};

/**
 * EOL予定日までのデータを拡張する（不足年次を空データで補完）
 *
 * @param data 変換済みデータ
 * @param maxYearIndex EOL予定日までの最大年次
 * @returns 拡張されたデータ
 */
export const extendQuantityAndPriceDataForEOL = (
  data: { [key: string]: any },
  maxYearIndex: number
): { [key: string]: any } => {
  const extendedData = { ...data };

  const createEmptyMonths = () => {
    const months: any = {};
    const monthOrder = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    monthOrder.forEach((m, idx) => {
      months[String(idx + 1)] = { month: m, quantity: 0, price: 0 };
    });
    return months;
  };

  for (let year = 1; year <= maxYearIndex; year++) {
    if (!extendedData[String(year)]) {
      extendedData[String(year)] = {
        quantity: 0,
        unitPrice: 0,
        salesPrice: 0,
        months: createEmptyMonths(),
      };
    }
  }

  return extendedData;
};

/**
 * EOL予定日までの最大年次インデックスを計算する
 *
 * @param baseStartYear 起算年
 * @param projectStartDate プロジェクト開始日
 * @param projectEndDate プロジェクト終了日（EOL予定日）
 * @param dataYearCount 変換済みデータの年次数
 * @returns 最大年次インデックス
 */
export const getMaxYearIndexByEOL = (
  baseStartYear: number,
  projectStartDate?: string | null,
  projectEndDate?: string | null,
  dataYearCount?: number
): number => {
  if (!projectEndDate || !projectStartDate) return dataYearCount ?? 1;

  const eolYear = parseInt(projectEndDate.split("-")[0], 10);
  const eolMonth = parseInt(projectEndDate.split("-")[1], 10);
  const startDate = new Date(projectStartDate);
  const startYear = startDate.getFullYear();
  const startDateMonth = startDate.getMonth() + 1;
  const fiscalYearBase = startDateMonth >= 4 ? startYear : startYear - 1;
  const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
  const maxYear = eolFiscalYear - fiscalYearBase + 1;
  return maxYear > 0 ? maxYear : 1;
};

/**
 * SpreadSheetデータを販売数量・販売単価データに変換（1パーツ分）
 *
 * @param values SpreadSheetから取得した2D配列 [headerRow, quantityRow, priceRow]
 * @param baseStartYear 起算年
 * @param projectData プロジェクトデータ
 * @param toast トースト通知関数
 * @param title トーストのタイトル
 * @returns 変換結果（データ + EOL最大年次）
 */
export const spreadSheetDataToQuantityAndPrice = (
  values: any[][],
  baseStartYear: number,
  projectData: { projectStartDate?: string | null; projectEndDate?: string | null },
  _toast?: ToastFunction,
  _title?: string,
  isGeneralPurpose?: boolean,
): QuantityAndPriceConversionResult => {
  const result: any = {};
  const conversionErrors: string[] = [];
  const conversionWarnings: string[] = [];

  if (!values || values.length === 0) {
    return { data: result, maxYearByEOL: 1, errors: [], warnings: [] };
  }

  const header = values[0];
  const quantityRow = values[1];
  const priceRow = values[2];

  const startMonth = projectData?.projectStartDate
    ? parseInt(projectData.projectStartDate.split("-")[1], 10)
    : 4;

  // 開始月が1〜3月の場合、年度1は開始月〜3月のみを保持する（画面・DB保存データと同じ構成）
  const createEmptyMonths = (yearIndex: number) => {
    const months: any = {};
    const monthOrder = yearIndex === 1 && startMonth <= 3
      ? Array.from({ length: 3 - startMonth + 1 }, (_, i) => startMonth + i)
      : [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    monthOrder.forEach((m, idx) => {
      months[String(idx + 1)] = { month: m, quantity: 0, price: 0 };
    });
    return months;
  };

  const addYear = (index: number) => {
    result[String(index)] = {
      quantity: 0,
      unitPrice: 0,
      salesPrice: 0,
      months: createEmptyMonths(index),
    };
  };

  let currentYearIndex = 1;
  addYear(currentYearIndex);

  const maxYearMonth = baseStartYear + 10;

  // EOL予定日の情報を取得
  const projectEndDate = projectData?.projectEndDate;
  const eolYear = projectEndDate ? parseInt(projectEndDate.split("-")[0], 10) : null;
  const eolMonth = projectEndDate ? parseInt(projectEndDate.split("-")[1], 10) : null;
  const eolYearMonth = eolYear && eolMonth ? eolYear * 100 + eolMonth : null;

  let eolWarningShown = false;

  for (let i = 1; i < header.length; i++) {
    const [yStr, mStr] = header[i].split("/");
    const year = parseInt(yStr);
    const month = parseInt(mStr);

    if (year === maxYearMonth && month > 3) {
      conversionWarnings.push(i18n.t(`${T_PREFIX}.message.eolMonthDataNotSynced`, { yearMonth: maxYearMonth }));
      break;
    }

    // EOL予定日を超える年月のデータは0として連携
    const currentYearMonth = year * 100 + month;
    const isAfterEOL = eolYearMonth !== null && currentYearMonth > eolYearMonth;

    if (isAfterEOL && !eolWarningShown) {
      conversionWarnings.push(i18n.t(`${T_PREFIX}.message.eolDataSetToZero`, { endDateFieldName: i18n.t('features.InvestmentScreening.BasicInfo.field.projectEndDate'), eolYear, eolMonth }));
      eolWarningShown = true;
    }

    const fiscalOffset = year > baseStartYear
      ? year - baseStartYear + (month >= 4 ? 0 : -1)
      : 0;

    const yearIndex = 1 + fiscalOffset;

    // EOL予定日の年度を超える年次はスキップ
    if (eolYear && eolMonth) {
      const eolFiscalYear = eolMonth >= 4 ? eolYear : eolYear - 1;
      const maxYearIndex = eolFiscalYear - baseStartYear + 1;
      if (maxYearIndex > 0 && yearIndex > maxYearIndex) {
        continue;
      }
    }

    if (!result[String(yearIndex)]) addYear(yearIndex);

    const yearMonths = result[String(yearIndex)].months;
    const monthKey = Object.keys(yearMonths).find(k => yearMonths[k].month === month);
    if (!monthKey) continue;

    const qRaw = (quantityRow?.[i] || "0").toString().replace(/,/g, "");
    const pRaw = (priceRow?.[i] || "0").toString().replace(/,/g, "");
    let q: number = Number(qRaw);
    let p: number = Number(pRaw);

    // EOL予定日以降のデータは0として連携
    if (isAfterEOL) {
      q = 0;
      p = 0;
    }

    if (isNaN(q)) {
      conversionErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
        year, month: String(month).padStart(2, "0"),
        label: i18n.t(`${T_PREFIX}.label.${isGeneralPurpose ? "quantityAssumed" : "quantitySales"}`),
        value: qRaw,
      }));
      q = 0;
    }
    if (isNaN(p)) {
      conversionErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
        year, month: String(month).padStart(2, "0"),
        label: i18n.t(`${T_PREFIX}.label.${isGeneralPurpose ? "priceAssumed" : "priceSales"}`),
        value: pRaw,
      }));
      p = 0;
    }

    result[String(yearIndex)].months[monthKey] = {
      month,
      quantity: q,
      price: p,
    };
  }

  // EOL最大年次を計算
  const maxYearByEOL = getMaxYearIndexByEOL(
    baseStartYear,
    projectData?.projectStartDate,
    projectData?.projectEndDate,
    Object.keys(result).length
  );

  // EOL予定日までのデータを拡張
  const extendedData = extendQuantityAndPriceDataForEOL(result, maxYearByEOL);

  return { data: extendedData, maxYearByEOL, errors: conversionErrors, warnings: conversionWarnings };
};

/**
 * 複数パーツの販売数量・販売単価データを1つのシート用2D配列に変換
 *
 * レイアウト:
 *   [partNo1]
 *   [header]   YYYY/MM  YYYY/MM  ...
 *   [販売数量]  100      200      ...
 *   [販売単価]  10.5     10.5     ...
 *   （空行）
 *   [partNo2]
 *   ...
 *
 * @param tables 複数パーツのテーブル配列
 * @param baseStartYear 起算年
 * @param projectStartDate プロジェクト開始日
 * @param isGeneralPurpose 汎用品フラグ
 * @param partNoList displayPartNo表示解決用のPartNo一覧（No.435対応）
 * @returns 2D配列
 */
export const multiPartQuantityAndPriceToSpreadSheetData = (
  tables: QuantityAndPriceMonthTable[],
  baseStartYear: number,
  projectStartDate?: string | null,
  isGeneralPurpose?: boolean,
  partNoList?: Pick<PartNo, "partNo" | "displayPartNo">[] | null
): any[][] => {
  const allData: any[][] = [];

  tables.forEach((table, idx) => {
    // パーツNo.ラベル行（displayPartNoが登録済みならそちらを表示、No.435対応）
    allData.push([resolveDisplayPartNoByKey(table.partNo, partNoList)]);

    // 1パーツ分のデータ（header, quantity, price）
    const partData = quantityAndPriceToSpreadSheetData(table, baseStartYear, projectStartDate, isGeneralPurpose);
    allData.push(...partData);

    // パーツ間に空行を挿入（最後のパーツ以外）
    if (idx < tables.length - 1) {
      allData.push([]);
    }
  });

  // 全行の列数を最大列数に統一（SpreadSheet APIのrangeが先頭行の列数で決まるため）
  const maxCols = Math.max(...allData.map(row => row.length), 0);
  if (maxCols > 0) {
    return allData.map(row => {
      if (row.length < maxCols) {
        return [...row, ...Array(maxCols - row.length).fill("")];
      }
      return row;
    });
  }

  return allData;
};

/**
 * 複数パーツ結合データの変換結果
 */
export interface MultiPartConversionResult {
  /** partNo → 変換済みデータのマップ */
  results: Map<string, QuantityAndPriceConversionResult>;
  /** パース時のエラー（partNo不一致など） */
  errors: string[];
  /** 警告メッセージ（EOL超過など、正常動作の通知） */
  warnings: string[];
}

/**
 * 1つのシートから複数パーツの販売数量・販売単価データを読み取る
 *
 * シートレイアウト想定:
 *   Row: [partNo]
 *   Row: [header]   YYYY/MM  YYYY/MM  ...
 *   Row: [販売数量]  100      200      ...
 *   Row: [販売単価]  10.5     10.5     ...
 *   Row: （空行）
 *   Row: [partNo2]
 *   ...
 *
 * 各パーツは4行（partNo、header、数量、単価）+ 空行区切り
 *
 * @param values SpreadSheetから取得した2D配列
 * @param partNos 期待するパーツNo.のリスト
 * @param baseStartYear 起算年
 * @param projectData プロジェクトデータ
 * @param toast トースト関数
 * @param title トーストタイトル
 * @returns パーツごとの変換結果マップ
 */
export const spreadSheetDataToMultiPartQuantityAndPrice = (
  values: any[][],
  partNos: string[],
  baseStartYear: number,
  projectData: { projectStartDate?: string | null; projectEndDate?: string | null },
  _toast?: ToastFunction,
  _title?: string,
  isGeneralPurpose?: boolean,
): MultiPartConversionResult => {
  const results = new Map<string, QuantityAndPriceConversionResult>();
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!values || values.length === 0) {
    return { results, errors, warnings };
  }

  // 空行で区切られたブロックに分割
  const blocks: any[][][] = [];
  let currentBlock: any[][] = [];

  for (const row of values) {
    const isEmpty = !row || row.length === 0 || row.every((cell: any) => cell === "" || cell === null || cell === undefined);
    if (isEmpty) {
      if (currentBlock.length > 0) {
        blocks.push(currentBlock);
        currentBlock = [];
      }
    } else {
      currentBlock.push(row);
    }
  }
  if (currentBlock.length > 0) {
    blocks.push(currentBlock);
  }

  // 各ブロックをパース
  for (const block of blocks) {
    if (block.length < 4) {
      // partNo行 + header行 + 数量行 + 単価行 の最低4行必要
      errors.push(i18n.t(`${T_PREFIX}.message.blockRowCountInsufficient`, { count: block.length }));
      continue;
    }

    // 1行目: partNo
    const partNo = String(block[0][0] || "").trim();
    if (!partNo) {
      errors.push(i18n.t(`${T_PREFIX}.message.emptyPartNoSkipped`));
      continue;
    }

    // 残り3行（header, quantity, price）を変換
    const dataRows = block.slice(1); // [header, quantity, price]

    // ヘッダーバリデーション
    const headerCells = dataRows[0]?.slice(1) || [];
    if (!validateQuantityAndPriceHeader(headerCells)) {
      errors.push(`${partNo}: ${i18n.t(`${T_PREFIX}.message.headerInvalid`)}`);
      continue;
    }

    // データ変換
    const conversionResult = spreadSheetDataToQuantityAndPrice(
      dataRows,
      baseStartYear,
      projectData,
      undefined,
      undefined,
      isGeneralPurpose,
    );

    // パーツごとの変換エラーを集約（パーツNo.をプレフィックスとして付加）
    for (const err of conversionResult.errors) {
      errors.push(`${partNo}: ${err}`);
    }
    for (const warn of conversionResult.warnings) {
      warnings.push(`${partNo}: ${warn}`);
    }

    results.set(partNo, conversionResult);
  }

  return { results, errors, warnings };
};
